import { f as UserRole, l as PermissionType, n as useAuthContext, u as ScopedRoleType } from "./AuthContext-BU6h5D0G.js";
import { a as GENETIC_TEST_TYPES, c as STI_TEST_TYPES, i as ESSENTIAL_CARE_TEST_TYPES, r as COVID_TEST_TYPES } from "./KitOrders-BOjeD1SH.js";
import { intersection } from "lodash-es";
//#region src/Authorization/Roles/roles.ts
/**
* Return all roles that the given user account represents.
*
* Roles form the basis of which content a user is able to view and interact with.
* Permission enforcement occurs at the API level, but we can identify which applications
* a user should be able to interact with by inspecting their user object.
*
* Any given user will be expected to have at least one role:
* At the very least, a user who has not had a Color test will have `UserRole.NO_TEST`.
* (And users who *have* had a test will have at least a `COVID_*` role and/or `GENETICS`)
*
* Users may have several separate roles!
* For example, providers and Color admins may still get Color tests for themselves.
*/
function getRoles(user) {
	const userIsNonDependentProxy = user.is_proxy && !user.is_dependent;
	const hasCovidServices = user.test_types.some((testType) => COVID_TEST_TYPES.includes(testType)) || user.has_user_reported_test_results || user.has_vaccination_status;
	const hasStiTest = user.test_types.some((testType) => STI_TEST_TYPES.includes(testType));
	const userHasEssentialCareTest = intersection(user.test_types, ESSENTIAL_CARE_TEST_TYPES).length > 0;
	const roleConditions = {
		[UserRole.USER_WITH_ACCOUNT]: !user.is_proxy,
		[UserRole.NO_TEST]: !user.test_types.length,
		[UserRole.COVID_NON_DEPENDENT_PROXY]: hasCovidServices && userIsNonDependentProxy,
		/**
		* FIXME: This relies on a temporary property that will be removed.
		* See https://getcolor.atlassian.net/browse/ANTI-286
		*/
		[UserRole.COVID_FULL_USER_OR_DEPENDENT]: hasCovidServices && !userIsNonDependentProxy,
		[UserRole.ESSENTIAL_CARE_USER]: userHasEssentialCareTest,
		[UserRole.CARE_FULL_USER_OR_DEPENDENT]: userHasEssentialCareTest || user.deprecated_has_been_consulted_for_paxlovid,
		[UserRole.STI_TEST_USER]: hasStiTest,
		[UserRole.COVID_SITE_MANAGER]: user.is_covid_site_manager,
		[UserRole.GENETICS]: user.test_types.some((testType) => GENETIC_TEST_TYPES.includes(testType)),
		[UserRole.PROVIDER]: user.is_provider,
		[UserRole.COLOR_EMPLOYEE]: user.is_color_employee,
		[UserRole.DASHBOARD_VIEWER]: user.is_dashboard_viewer,
		[UserRole.ACCESS_MANAGER]: user.is_access_manager,
		[UserRole.SUPPORT_STAFF]: user.is_support_staff,
		[UserRole.SUPPORT_ADMIN]: user.is_support_admin,
		[UserRole.SAMPLE_REVIEWER]: user.is_sample_reviewer,
		[UserRole.ORDERING_PHYSICIAN]: user.is_ordering_physician,
		[UserRole.GENETIC_COUNSELOR]: user.is_genetic_counselor,
		[UserRole.EXTERNAL_CLASSIFICATION_REVIEWER]: user.is_external_classification_reviewer,
		[UserRole.INTERNAL_CLASSIFICATION_REVIEWER]: user.is_internal_classification_reviewer,
		[UserRole.VARIANT_REVIEWER]: user.is_variant_reviewer,
		[UserRole.VARIANT_REVIEWER_MANAGER]: user.is_variant_reviewer_manager,
		[UserRole.GENETICS_BOARD_MEMBER]: user.is_genetics_board_member,
		[UserRole.PATHOLOGIST]: user.is_pathologist,
		[UserRole.COLLECTION_SITE_ADMIN]: user.collection_site_permissions?.length > 0 || user.roles?.some((role) => role.scope_content_type === "collection-site" || role.role_type === ScopedRoleType.VACCINATION_PLATFORM_SUPER_ADMIN),
		[UserRole.FULFILLMENT_STAFF]: user.is_fulfillment_staff,
		[UserRole.CONTACT_MANAGER]: user.roles?.some((role) => role.role_type === ScopedRoleType.CONTACT_MANAGER),
		[UserRole.ELIGIBILITY_DATA_MANAGER]: user.roles?.some((role) => role.role_type === ScopedRoleType.ELIGIBILITY_DATA_MANAGER),
		[UserRole.VACCINATION_PLATFORM_SUPER_ADMIN]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_SUPER_ADMIN),
		[UserRole.VACCINATION_PLATFORM_STAFF_MANAGER]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_STAFF_MANAGER),
		[UserRole.VACCINATION_PLATFORM_SITE_MANAGER]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_SITE_MANAGER),
		[UserRole.VACCINATION_PLATFORM_SCRIBE]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_SCRIBE),
		[UserRole.VACCINATION_PLATFORM_SITE_STAFF]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_SITE_STAFF),
		[UserRole.VACCINATION_PLATFORM_VACCINATING_CLINICIAN]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_VACCINATING_CLINICIAN),
		[UserRole.VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER),
		[UserRole.VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER]: user.roles?.some((role) => role.role_type === ScopedRoleType.VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER),
		[UserRole.LAB_STAFF]: user.is_lab_staff
	};
	return Object.values(UserRole).filter((role) => roleConditions[role]);
}
//#endregion
//#region src/Authorization/Roles/utils.ts
function getFullName(user) {
	return `${user.first_name} ${user.last_name}`;
}
function hasRole(user, role) {
	return getRoles(user).includes(role);
}
function hasAnyRoles(user, roles) {
	const userRoles = getRoles(user);
	return roles.some((role) => userRoles.includes(role));
}
//#endregion
//#region src/Authorization/useAuthorization/isUserAuthorized.ts
function hasPermissions(user, permissions) {
	return permissions.every((permission) => Boolean(user.permissions[permission]));
}
function isUserAuthorized(currentUser, authProps) {
	const { allowedRoles = [], requiredPermissions = [], signedOutUserIsAllowed = false } = authProps;
	if (currentUser) {
		const userHasAllowedRoles = allowedRoles.some((role) => hasRole(currentUser, role) || currentUser.dependents.some((dependent) => hasRole(dependent, role)));
		const userHasPermissions = hasPermissions(currentUser, requiredPermissions);
		if (allowedRoles.length > 0 && requiredPermissions.length > 0) throw new Error("Both roles and permissions are specified.  You likely only want one set.");
		else if (allowedRoles.length > 0) return userHasAllowedRoles;
		else if (requiredPermissions.length > 0) return userHasPermissions;
		return true;
	}
	return signedOutUserIsAllowed;
}
//#endregion
//#region src/Authorization/useAuthorization/useAuthorization.ts
function useAuthorization(props) {
	const { hasInitializedUser, currentUser } = useAuthContext();
	const { allowedRoles, requiredPermissions, signedOutUserIsAllowed } = props;
	if (hasInitializedUser) return isUserAuthorized(currentUser, {
		allowedRoles,
		requiredPermissions,
		signedOutUserIsAllowed
	}) ? PermissionType.ALLOWED : PermissionType.NOT_ALLOWED;
	return PermissionType.UNKNOWN;
}
//#endregion
export { hasRole as a, hasAnyRoles as i, isUserAuthorized as n, getRoles as o, getFullName as r, useAuthorization as t };

//# sourceMappingURL=Authorization-IGKkSucl.js.map